// ─── Submission resilience ────────────────────────────────────────────────────
// Album submission renders + uploads a multi-MB PDF entirely in the foreground
// tab — work that can take 15–30s. Mobile browsers throttle and eventually
// suspend backgrounded tabs, so if the customer locks their phone or switches
// apps mid-submit the render silently halts and the submit errors out ("it
// stopped"). These helpers keep the work alive as far as the platform allows
// and make accidental navigation explicit:
//
//   • acquireScreenWakeLock — holds a screen Wake Lock so device auto-lock
//     can't sleep the screen (and suspend the tab) during the render. The lock
//     is auto-dropped when a tab is backgrounded, so we re-acquire whenever the
//     tab returns to the foreground.
//   • installUnloadGuard — triggers the browser's native "Leave site?" prompt
//     if the customer tries to close/navigate while a submit is in flight.
//   • withRetry — retries an idempotent async step with backoff so a single
//     throttled-tab network blip doesn't fail the whole submission.
//
// NOTE: truly "rendering while the phone is fully off" is NOT achievable in the
// browser — a discarded tab runs no JS. That needs server-side rendering, a
// separate, larger change. These helpers cover the realistic cases: a glance
// away, an app switch, a momentary network stall, an accidental back-swipe.

interface WakeLockSentinelLike {
  release: () => Promise<void>;
  addEventListener: (type: 'release', cb: () => void) => void;
}
interface WakeLockNavigator {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
}

export interface WakeLockHandle {
  /** Release the lock and stop re-acquiring. Idempotent; safe in a `finally`. */
  release: () => Promise<void>;
}

/**
 * Acquire a screen Wake Lock and keep it held across foreground/background
 * transitions until `release()` is called. Resolves immediately (with a no-op
 * handle) on browsers without the Wake Lock API — callers never need to branch.
 */
export async function acquireScreenWakeLock(): Promise<WakeLockHandle> {
  const nav = (typeof navigator !== 'undefined' ? navigator : undefined) as unknown as
    | WakeLockNavigator
    | undefined;
  const wl = nav?.wakeLock;
  if (!wl) {
    // Unsupported (older iOS Safari, some Android browsers) — non-fatal no-op.
    return { release: async () => {} };
  }

  let sentinel: WakeLockSentinelLike | null = null;
  let released = false;

  const request = async () => {
    if (released || sentinel) return;
    try {
      sentinel = await wl.request('screen');
      // The OS auto-releases the lock when the tab is backgrounded; null it so
      // the visibility handler re-acquires on the next foreground.
      sentinel.addEventListener('release', () => {
        sentinel = null;
      });
    } catch {
      // Denied or transient — leave sentinel null; we retry on next foreground.
    }
  };

  const onVisibility = () => {
    if (!released && typeof document !== 'undefined' && document.visibilityState === 'visible') {
      void request();
    }
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  await request();

  return {
    async release() {
      released = true;
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
      }
      try {
        await sentinel?.release();
      } catch {
        /* already released — ignore */
      }
      sentinel = null;
    },
  };
}

/**
 * Install a `beforeunload` guard that prompts the browser's "Leave site?"
 * dialog. Returns an uninstall function — call it the moment the submit
 * settles (success OR failure) so normal navigation isn't blocked.
 */
export function installUnloadGuard(): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    // Legacy browsers require returnValue to be set for the prompt to show.
    e.returnValue = '';
  };
  window.addEventListener('beforeunload', handler);
  return () => window.removeEventListener('beforeunload', handler);
}

export interface RetryOptions {
  /** Retry attempts AFTER the first try. `2` ⇒ up to 3 total attempts. */
  retries?: number;
  /** Backoff in ms before attempt N (1-based). Default: N × 1000ms. */
  backoffMs?: (attempt: number) => number;
  /** Return false to stop retrying a given error (e.g. a permanent 4xx). */
  shouldRetry?: (err: unknown) => boolean;
  /** Side-effect on each retry (e.g. surface "Nouvel essai…" to the user). */
  onRetry?: (err: unknown, attempt: number) => void;
}

/**
 * Run an idempotent async step, retrying transient failures with backoff.
 * The submission's terminal steps (finalize, submit) are all idempotent —
 * re-running them simply re-writes the same row — so retrying is safe and
 * turns a throttled-tab network blip into a recoverable hiccup instead of a
 * hard failure the customer sees as "it stopped".
 */
export async function withRetry<T>(
  fn: (attempt: number) => Promise<T>,
  opts: RetryOptions = {},
): Promise<T> {
  const retries = opts.retries ?? 2;
  const backoff = opts.backoffMs ?? ((a) => a * 1000);
  // Default: retry transient failures, but NOT a permanent 4xx — a malformed /
  // too-large / forbidden request never succeeds on a re-send, so retrying it
  // just wastes time and hides the real error (e.g. a 413 "PDF too large").
  // 408 (timeout) and 429 (rate-limit) ARE transient, so they still retry.
  const shouldRetry = opts.shouldRetry ?? ((err: unknown) => {
    const status = (err as { status?: number })?.status;
    if (typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429) {
      return false;
    }
    return true;
  });
  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn(attempt);
    } catch (err) {
      lastErr = err;
      if (attempt === retries || !shouldRetry(err)) throw err;
      opts.onRetry?.(err, attempt + 1);
      await new Promise((r) => setTimeout(r, backoff(attempt + 1)));
    }
  }
  throw lastErr;
}
